import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { ScrollArea } from './ui/scroll-area';
import { Badge } from './ui/badge';
import { Loader2, Activity, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { cn } from '../lib/utils';

const fmtTime = (s) => {
  if (!s) return '';
  try { return new Date(s).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' }); }
  catch { return s; }
};

const STATUS_STYLE = {
  completed: { dot: 'bg-emerald-500', label: 'Completed' },
  failed:    { dot: 'bg-red-500',     label: 'Failed' },
  running:   { dot: 'bg-sky-500 animate-pulse', label: 'Running' },
  pending:   { dot: 'bg-amber-500',   label: 'Pending' },
  undone:    { dot: 'bg-slate-500',   label: 'Undone' },
};

/**
 * Phase 5 — Deck timeline modal.
 * Fetches GET /api/ai/v2/actions/{action_run_id}/timeline and renders
 * the action_run + audit log in chronological order.
 */
export default function DeckTimelineModal({ open, onClose, actionRunId, deckItem }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!open || !actionRunId) return;
    let cancelled = false;
    setLoading(true); setErr(null); setData(null);
    (async () => {
      try {
        const res = await fetch(`/api/ai/v2/actions/${encodeURIComponent(actionRunId)}/timeline`, { credentials: 'include' });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const body = await res.json();
        if (!cancelled) setData(body);
      } catch (e) {
        if (!cancelled) setErr(e.message || String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, actionRunId]);

  const run = data?.action_run;
  const audit = data?.audit || [];
  const status = (run?.status || 'pending').toLowerCase();
  const stStyle = STATUS_STYLE[status] || STATUS_STYLE.pending;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent data-testid="deck-timeline-modal" className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Activity className="w-4 h-4 text-violet-500" />
            Action timeline
            {actionRunId && <Badge variant="outline" className="font-mono text-[10px]">{actionRunId}</Badge>}
          </DialogTitle>
        </DialogHeader>

        {loading && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground py-8 justify-center">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading timeline…
          </div>
        )}
        {err && (
          <div className="text-sm text-red-500 flex items-center gap-2 py-4">
            <AlertTriangle className="w-4 h-4" /> Failed to load: {err}
          </div>
        )}

        {!loading && !err && run && (
          <div className="space-y-3">
            <div className="rounded-lg border bg-card p-3 text-sm">
              <div className="flex items-center gap-2">
                <span className={cn('w-2.5 h-2.5 rounded-full', stStyle.dot)} />
                <span className="font-medium">{stStyle.label}</span>
                <Badge variant="secondary" className="ml-auto font-mono text-[10px]">
                  {run.capability_id || deckItem?.capability_id || '—'}
                </Badge>
              </div>
              <div className="grid grid-cols-2 gap-2 mt-2 text-xs text-muted-foreground">
                <div>Started: {fmtTime(run.started_at || run.created_at)}</div>
                <div>Finished: {fmtTime(run.completed_at)}</div>
                <div>Surface: {run.surface || '—'}</div>
                <div>Source: {run.source || '—'}</div>
              </div>
              {run.error && (
                <div className="mt-2 text-xs rounded bg-red-500/10 text-red-600 p-2">
                  {run.error.error_code ? `${run.error.error_code}: ` : ''}{run.error.message || JSON.stringify(run.error)}
                </div>
              )}
              {run.diff_summary && (
                <div className="mt-2 text-xs text-muted-foreground">
                  Diff: {Object.entries(run.diff_summary).map(([k,v]) => `${k}=${v}`).join(' · ')}
                </div>
              )}
            </div>

            <div>
              <div className="text-xs text-muted-foreground uppercase tracking-wide mb-2">Audit log</div>
              <ScrollArea className="max-h-72 pr-2">
                {audit.length === 0 && (
                  <div className="text-xs text-muted-foreground p-3">No audit entries.</div>
                )}
                <ol className="space-y-2">
                  {audit.map((a, i) => (
                    <li key={a.audit_id || i} className="flex items-start gap-2 text-xs" data-testid={`deck-timeline-audit-${i}`}>
                      <CheckCircle2 className="w-3.5 h-3.5 mt-0.5 text-emerald-500 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{a.action || a.event_type || 'event'}</span>
                          <span className="text-muted-foreground">{fmtTime(a.created_at || a.timestamp)}</span>
                        </div>
                        {a.summary && <div className="text-muted-foreground">{a.summary}</div>}
                        {a.metadata && Object.keys(a.metadata || {}).length > 0 && (
                          <div className="text-[10px] text-muted-foreground/80 truncate">
                            {Object.entries(a.metadata).slice(0, 4).map(([k, v]) => `${k}=${typeof v === 'object' ? JSON.stringify(v) : v}`).join(' · ')}
                          </div>
                        )}
                      </div>
                    </li>
                  ))}
                </ol>
              </ScrollArea>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
